"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.FeatureRoot = exports.DEFAULT_APPLICATION_OPTIONS = void 0;
const FeatureHookManager_1 = require("./FeatureHookManager");
const FeatureResourceManager_1 = require("./FeatureResourceManager");
const FeatureServicesManager_1 = require("./FeatureServicesManager");
const Scriptable_1 = require("../Scriptable");
const feature_1 = require("../log/feature");
/**
 * The default application options for a Feature application.
 */
exports.DEFAULT_APPLICATION_OPTIONS = {
    name: 'feature',
    frontend: 'https://feature.com',
    domain: 'feature.com',
    rootDomain: 'feature.com',
    services: {},
};
/**
 * The most basic root implementation for a Feature application.
 *
 * @remarks
 * This is the main class for managing a Feature-based application. It has helper methods
 * for using and configuring features, and preparing and running the service.
 *
 * There are three prepare methods, early, middle, and late, and they each call their corresponding
 * method on each of the used features. This is to allow features to create resources, hooks, or
 * perform other actions based on what resources, hooks, etc other features in the system add when
 * they're constructed.
 *
 * We split preparation into 3 phases to account for one feature needing to prepare after another
 * feature. Because of this, it is essential you not use {@link FeatureRoot.prepareLate} unless you
 * <i>absolutely need to.</i>
 */
class FeatureRoot {
    constructor(appOptions, deps) {
        this.features = {};
        this.appOptions = appOptions;
        const hookManager = new FeatureHookManager_1.FeatureHookManager();
        const resources = new FeatureResourceManager_1.FeatureResourceManager();
        const services = new FeatureServicesManager_1.FeatureServicesManager(Object.assign(Object.assign({}, appOptions.services), { root: this }));
        // @ts-ignore fix the deps optional requirement above
        this.deps = Object.assign({ hookManager,
            resources,
            services, root: this }, deps);
    }
    /**
     * Use a feature.
     * @remarks
     * Also means, "add a feature to the project."
     * @param Feature - the feature to add
     * @param options - the options to use for that feature
     */
    useFeature(Feature, options) {
        (0, feature_1.debug)('using feature: %s', Feature.package);
        if (this.features[Feature.package]) {
            throw new Error(`Attempting to use feature '${Feature.package}' multiple times.`);
        }
        this.features[Feature.package] = new Feature(options, this.deps);
    }
    /**
     * Use multiple features at once.
     * @param features - an array of feature use definitions representing features to use
     */
    use(features) {
        for (const feature of features) {
            if (Array.isArray(feature)) {
                this.useFeature(feature[0], feature[1]);
            }
            else {
                this.useFeature(feature, {});
            }
        }
    }
    /**
     * Gets a feature by its package name.
     * @param name - the package name of the feature to get
     * @returns the feature if found, otherwise undefined
     */
    getFeature(name) {
        return this.features[name] ? this.features[name] : undefined;
    }
    requireFeature(nameOrFeature) {
        let result;
        if (typeof nameOrFeature === 'string') {
            result = this.getFeature(nameOrFeature);
        }
        else {
            result = this.getFeature(nameOrFeature.package);
        }
        if (!result) {
            throw new Error(`Feature '${name}' could not be found.`);
        }
        return result;
    }
    runLifecycle(key) {
        return __awaiter(this, void 0, void 0, function* () {
            (0, feature_1.debug)('running lifecycle: %s', key);
            yield Promise.all(Object.keys(this.features).map(featureName => {
                const feature = this.features[featureName];
                const callback = feature[key];
                if (callback) {
                    (0, feature_1.debug)('...in %s', featureName);
                    return callback.call(feature);
                }
            }));
        });
    }
    /**
     * Runs early preparations.
     */
    prepareEarly() {
        return __awaiter(this, void 0, void 0, function* () {
            return this.runLifecycle('prepareEarly');
        });
    }
    /**
     * Runs middle preparations.
     */
    prepareMiddle() {
        return __awaiter(this, void 0, void 0, function* () {
            return this.runLifecycle('prepareMiddle');
        });
    }
    /**
     * Runs late preparations.
     */
    prepareLate() {
        return __awaiter(this, void 0, void 0, function* () {
            return this.runLifecycle('prepareLate');
        });
    }
    /**
     * Runs all three preparation phases, if not already prepared.
     * @remarks
     * This function has internal checks to make sure we only prepare a Feature root once,
     * as preparing it multiple times would result in duplicate resources being added.
     */
    prepare() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.preparePromise) {
                (0, feature_1.debug)('Already preparing / prepared. Returning promise.');
                return this.preparePromise;
            }
            else {
                this.preparePromise = this.prepareEarly()
                    .then(() => this.prepareMiddle())
                    .then(() => this.prepareLate());
                return this.preparePromise;
            }
        });
    }
    /** Runs all three beforeDeploy phases. */
    beforeDeploy() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.runLifecycle('beforeDeployEarly');
            yield this.runLifecycle('beforeDeployMiddle');
            yield this.runLifecycle('beforeDeployLate');
        });
    }
    /** Runs all three afterDeploy phases. */
    afterDeploy() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.runLifecycle('afterDeployEarly');
            yield this.runLifecycle('afterDeployMiddle');
            yield this.runLifecycle('afterDeployLate');
        });
    }
    /**
     * Allows other features to add commands and options to the Feature command line.
     * @remarks
     * See the documentation for the feature-scripts feature for more information.
     * @param argv - the yargs instance to configure
     * @returns a promise with the configured yargs instance
     */
    bootstrapScripts(argv) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.prepare();
            return this.deps.hookManager.mutateHook(Scriptable_1.CLI_HOOK, argv, undefined);
        });
    }
}
exports.FeatureRoot = FeatureRoot;
//# sourceMappingURL=FeatureRoot.js.map